import React, { useEffect, useState } from "react";
import { CloudOff, RefreshCw, CheckCircle2, Download } from "lucide-react";
import { onSyncEvent, getQueueSummary, syncNow } from "@/lib/offlineSync";

/**
 * Small status pill:
 *  - shows "Offline" when navigator says we're offline
 *  - shows "Syncing…" while a sync is in progress
 *  - shows "Queue: N" when items are waiting to be sent
 * Also handles a one-time "Install app" prompt when the browser fires
 * `beforeinstallprompt` (Android Chrome / desktop).
 */
const OfflineStatus = () => {
  const [online, setOnline] = useState(navigator.onLine);
  const [queue, setQueue] = useState({ addresses: 0, scans: 0 });
  const [syncing, setSyncing] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installDismissed, setInstallDismissed] = useState(
    () => localStorage.getItem("pwa_install_dismissed") === "1"
  );

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const refresh = async () => {
      const s = await getQueueSummary().catch(() => ({ addresses: 0, scans: 0 }));
      if (mounted) setQueue(s);
    };
    refresh();
    const unsub = onSyncEvent((e) => {
      if (e.type === "sync-start") setSyncing(true);
      if (e.type === "sync-progress" || e.type === "sync-done") refresh();
      if (e.type === "sync-done") setSyncing(false);
    });
    const t = setInterval(refresh, 15000);
    return () => { mounted = false; unsub(); clearInterval(t); };
  }, []);

  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const pending = queue.addresses + queue.scans;

  const install = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    try { await installPrompt.userChoice; } catch { /* ignore */ }
    setInstallPrompt(null);
    setInstallDismissed(true);
    localStorage.setItem("pwa_install_dismissed", "1");
  };

  const dismissInstall = () => {
    setInstallDismissed(true);
    localStorage.setItem("pwa_install_dismissed", "1");
  };

  // Nothing to show
  if (online && pending === 0 && !syncing && (!installPrompt || installDismissed)) return null;

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] pointer-events-none flex flex-col items-center gap-2 w-max max-w-[92vw]">
      {installPrompt && !installDismissed && (
        <div className="pointer-events-auto flex items-center gap-3 rounded-full bg-brand-navy text-white pl-3 pr-1.5 py-1.5 shadow-lg border border-white/10"
             data-testid="pwa-install-toast">
          <Download className="w-4 h-4 text-emerald-400" />
          <span className="text-xs font-medium">Install MY PORTAL app</span>
          <button onClick={install}
                  className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold px-3 py-1"
                  data-testid="pwa-install-btn">
            Install
          </button>
          <button onClick={dismissInstall} className="text-white/50 hover:text-white text-xs px-2">
            ✕
          </button>
        </div>
      )}
      {(!online || pending > 0 || syncing) && (
        <div
          className={`pointer-events-auto flex items-center gap-2 rounded-full px-4 py-1.5 shadow-lg text-xs font-medium
            ${!online ? "bg-amber-500 text-white" : syncing ? "bg-brand-navy text-white" : "bg-emerald-600 text-white"}`}
          data-testid="offline-status-pill"
        >
          {!online ? (
            <>
              <CloudOff className="w-3.5 h-3.5" />
              <span>Offline{pending > 0 ? ` — ${pending} pending` : ""}</span>
            </>
          ) : syncing ? (
            <>
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              <span>Syncing {pending} item{pending !== 1 ? "s" : ""}…</span>
            </>
          ) : (
            <>
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>{pending} waiting to sync</span>
              <button onClick={() => syncNow()} className="underline underline-offset-2" data-testid="offline-sync-now">
                Retry
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default OfflineStatus;
